import { TDisplayDefinitionEntryBoolean } from '@internal/utils/displayDefinition/entry/DisplayDefinitionEntryBoolean'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { MachineAttribute } from '@internal/utils/machine/MachineAttribute'
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  TextField,
} from '@mui/material'
import { TFilterOption } from '@renderer-ui-library/molecules'
import { useCustomTags } from '@renderer-ui-library/molecules/AutoComplete/useCustomTags'
import { SearchFilterCategory } from '@renderer-ui-library/organisms'
import { useSearchParams } from 'blocks/SearchResultsBlock/useSearchParams'
import { useSearchResultsRouting } from 'blocks/SearchResultsBlock/useSearchResultsRouting'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useMemo } from 'react'
import { tracker } from 'utils/tracking/tracker'
import { useAutocompleteItem } from './AutocompleteItem'

type Props = {
  options: TDisplayDefinitionEntryBoolean[]
}

export const FeaturesFilter: React.FC<Props> = React.memo((props) => {
  const searchParams = useSearchParams()
  const { gotoSearchResults } = useSearchResultsRouting()
  const { t } = useTranslation(localeNamespace.common)
  const { renderOption } = useAutocompleteItem()
  const { renderTags } = useCustomTags<TFilterOption, true>()
  const options = useMemo<TFilterOption[]>(
    () =>
      props.options
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(({ machineAttribute, label }) => ({
          label,
          id: machineAttribute,
          selected: searchParams.features.includes(machineAttribute),
        })),
    [props.options, searchParams.features]
  )

  const setFeatures = useCallback(
    (features: MachineAttribute[]) =>
      gotoSearchResults({
        ...searchParams,
        features,
      }),
    [gotoSearchResults, searchParams]
  )

  const handleChange = useCallback(
    (
      event: React.SyntheticEvent,
      value: TFilterOption[],
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<TFilterOption>
    ) => {
      const newFeatures = value.map((option) => option.id as MachineAttribute)

      const addedFeatures = newFeatures.filter(
        (feat) => !searchParams.features.includes(feat)
      )

      setFeatures(newFeatures)

      // only track the last selected feature
      if (addedFeatures.length > 0) {
        tracker.trackEvent({
          name: 'filter-applied',
          filter_placement: 'serp',
          filter_category: 'feature',
          filter_feature: addedFeatures[0],
          is_interaction: true,
        })
      }
    },
    [searchParams.features, setFeatures]
  )

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        placeholder={t(translations.searchResultsFiltersFeaturePlaceholder)}
        size='small'
      />
    ),
    [t]
  )

  const autocompleteValue = useMemo<TFilterOption[]>(() => {
    return options.filter((option) => option.selected)
  }, [options])

  return (
    <SearchFilterCategory
      title={t(translations.searchResultsFiltersFeatureLabel)}
    >
      <Autocomplete<TFilterOption, true>
        multiple
        disableCloseOnSelect
        limitTags={3}
        id='features-filter'
        options={options}
        renderOption={renderOption}
        renderInput={renderInput}
        value={autocompleteValue}
        onChange={handleChange}
        renderTags={renderTags}
        slotProps={{
          paper: {
            elevation: 10,
          },
        }}
      />
    </SearchFilterCategory>
  )
})

FeaturesFilter.displayName = 'FeaturesFilter'
